/* eslint-disable no-param-reassign */
import {createSlice, nanoid} from '@reduxjs/toolkit';
import {getConversationMessages, getConversations} from '../api/conversationsThunk';

const initialState = {
    panelOpen: false,
    activeTab: 'chat',
    messages: [],
    threads: [],
    currentConversationId: null,
    selectedDomain: null,
    loading: false,
    activitySteps: [],
    error: null
};

// Assistant messages are ordered segments of a turn: kind 'text' (markdown) or
// kind 'render' (a validated render-tool payload shown inline, e.g. a chart).
const lastUserIndexOf = (messages) => messages.findLastIndex((msg) => msg.role === 'user');

const aiChatSlice = createSlice({
    name: 'aiChat',
    initialState,
    reducers: {
        setPanelOpen(state, action) {
            state.panelOpen = action.payload;
        },
        setActiveTab(state, action) {
            state.activeTab = action.payload;
        },
        setSelectedDomain(state, action) {
            state.selectedDomain = action.payload;
        },
        appendUserMessage(state, action) {
            // A conversation_id is minted client-side on the first message of a
            // new chat (currentConversationId is null after startNewChat) and
            // reused for every later turn, so the backend can upsert the same
            // `conversations` row / LangGraph thread instead of creating a new
            // one per turn.
            if (!state.currentConversationId) {
                state.currentConversationId = nanoid();
            }
            state.messages.push({
                id: nanoid(),
                role: 'user',
                content: action.payload
            });
            state.error = null;
        },
        // Replaces this turn's streamed assistant messages with the authoritative
        // ordered segments from the final snapshot, reusing ids by position so
        // already-rendered rows (and charts) don't remount.
        finalizeTurn(state, action) {
            const segments = action.payload || [];
            if (segments.length > 0) {
                const turnStart = lastUserIndexOf(state.messages) + 1;
                const existing = state.messages.slice(turnStart);
                const rebuilt = segments.map((segment, index) => {
                    const previous = existing[index];
                    const id = previous?.kind === segment.kind ? previous.id : nanoid();
                    return segment.kind === 'render'
                        ? {
                            id, role: 'assistant', kind: 'render', content: '', ...segment
                        }
                        : {
                            id, role: 'assistant', kind: 'text', content: segment.content
                        };
                });
                state.messages.splice(turnStart, existing.length, ...rebuilt);
            }
            state.loading = false;
            state.error = null;
        },
        appendRenderMessage(state, action) {
            const {toolCallId, tool, payload} = action.payload;
            if (state.messages.some((msg) => msg.toolCallId === toolCallId)) return;
            state.messages.push({
                id: nanoid(), role: 'assistant', kind: 'render', content: '', toolCallId, tool, payload
            });
        },
        // Appends one token-by-token TEXT_MESSAGE_CONTENT delta, keyed by the
        // AG-UI messageId that started the stream. Consecutive deltas for the
        // same messageId always arrive in order, so they only ever need to
        // grow the last message in the list, not search the whole array.
        appendAssistantMessageDelta(state, action) {
            const {messageId, delta} = action.payload || {};
            if (!messageId || !delta) return;

            const lastMessage = state.messages.at(-1);
            if (lastMessage?.role === 'assistant' && lastMessage.aguiMessageId === messageId) {
                lastMessage.content += delta;
            } else {
                state.messages.push({
                    id: nanoid(),
                    role: 'assistant',
                    kind: 'text',
                    content: delta,
                    aguiMessageId: messageId
                });
            }
            state.error = null;
        },
        setLoading(state, action) {
            state.loading = action.payload;
            state.activitySteps = [];
            state.error = null;
        },
        addActivityStep(state, action) {
            const previous = state.activitySteps.at(-1);
            if (previous) previous.done = true;
            state.activitySteps.push({id: nanoid(), label: action.payload, done: false});
        },
        setError(state, action) {
            const {payload} = action;
            state.error = typeof payload === 'string'
                ? {
                    type: 'UNKNOWN',
                    statusCode: null,
                    rawMessage: payload,
                    retryable: true
                }
                : payload;
            state.loading = false;
            state.activitySteps = [];
        },
        startNewChat(state) {
            state.messages = [];
            state.currentConversationId = null;
            state.error = null;
            state.loading = false;
            state.activeTab = 'chat';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getConversations.fulfilled, (state, action) => {
                state.threads = action.payload;
            })
            .addCase(getConversationMessages.pending, (state) => {
                state.loading = true;
            })
            .addCase(getConversationMessages.fulfilled, (state, action) => {
                state.messages = action.payload.messages.map((message) => (
                    message.kind === 'render'
                        ? {
                            id: nanoid(),
                            role: 'assistant',
                            kind: 'render',
                            content: '',
                            tool: message.tool,
                            payload: message.payload
                        }
                        : {
                            id: nanoid(),
                            role: message.role,
                            kind: 'text',
                            content: message.content
                        }
                ));
                state.currentConversationId = action.payload.conversation_id;
                if (action.payload.domain_filter) state.selectedDomain = action.payload.domain_filter;
                state.activeTab = 'chat';
                state.loading = false;
                state.error = null;
            })
            .addCase(getConversationMessages.rejected, (state, action) => {
                state.loading = false;
                state.error = {
                    type: 'UNKNOWN',
                    statusCode: null,
                    rawMessage: action.error.message || 'Unable to load that conversation.',
                    retryable: true
                };
            });
    }
});

export const {
    setPanelOpen,
    setActiveTab,
    setSelectedDomain,
    appendUserMessage,
    appendAssistantMessageDelta,
    appendRenderMessage,
    finalizeTurn,
    setLoading,
    addActivityStep,
    setError,
    startNewChat
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
